// Monday Morning Report - Everything you need to know in one page!
import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Paper,
  Divider,
  IconButton
} from "@mui/material";
import {
  TrendingUp,
  Warning,
  CheckCircle,
  Email,
  Print,
  Refresh,
  PriorityHigh,
  AttachMoney,
  Celebration
} from "@mui/icons-material";
import { propertyService, paymentService, maintenanceService, leaseService, tenantService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

// Client-side report generation
function generateReport(properties, payments, maintenance, leases, tenants) {
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Occupancy
  const totalUnits = properties.length;
  const activeLeases = leases.filter(l => l.status === "active");
  const occupiedUnits = activeLeases.length;
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;

  // Money this week
  const recentPayments = payments.filter(p => {
    const pDate = p.createdAt?.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
    return pDate >= oneWeekAgo;
  });
  const collectedThisWeek = recentPayments
    .filter(p => p.status === "paid" || p.status === "completed")
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const pendingPayments = payments.filter(p => p.status === "pending");
  const totalPending = pendingPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

  // Maintenance issues
  const openMaintenance = maintenance.filter(m => m.status === "open" || m.status === "pending");
  const urgentMaintenance = openMaintenance.filter(m => m.priority === "high" || m.priority === "urgent");

  // Overall status
  let overall;
  if (occupancyRate >= 90 && pendingPayments.length === 0 && urgentMaintenance.length === 0) {
    overall = "Excellent";
  } else if (occupancyRate >= 75 && urgentMaintenance.length <= 1) {
    overall = "Good";
  } else {
    overall = "Needs attention";
  }

  // Priorities
  const priorities = [];
  if (pendingPayments.length > 0) {
    priorities.push(`Follow up on ${pendingPayments.length} pending payment(s) totaling $${totalPending.toLocaleString()}`);
  }
  if (urgentMaintenance.length > 0) {
    priorities.push(`Address ${urgentMaintenance.length} urgent maintenance request(s)`);
  }
  if (occupancyRate < 100) {
    const vacant = totalUnits - occupiedUnits;
    priorities.push(`Fill ${vacant} vacant unit(s) to improve occupancy`);
  }
  if (priorities.length === 0) {
    priorities.push("Everything is on track - keep up the great work!");
  }

  // Problems
  const problems = [];
  urgentMaintenance.forEach(m => {
    const prop = properties.find(p => p.id === m.propertyId);
    problems.push({
      property: prop?.address || "Unknown Property",
      issue: m.description || m.title || "Maintenance needed",
      urgency: "high",
    });
  });
  pendingPayments.slice(0, 3).forEach(p => {
    problems.push({
      property: "Payment",
      issue: `$${p.amount || 0} payment overdue`,
      urgency: "medium",
    });
  });

  // Wins
  const wins = [];
  if (collectedThisWeek > 0) {
    wins.push(`Collected $${collectedThisWeek.toLocaleString()} this week`);
  }
  const resolvedMaint = maintenance.filter(m => {
    const mDate = m.updatedAt?.toDate ? m.updatedAt.toDate() : new Date(m.updatedAt);
    return m.status === "resolved" && mDate >= oneWeekAgo;
  });
  if (resolvedMaint.length > 0) {
    wins.push(`Resolved ${resolvedMaint.length} maintenance request(s)`);
  }
  if (occupancyRate === 100) {
    wins.push("100% occupancy - all units filled!");
  }
  if (wins.length === 0) {
    wins.push("Portfolio is stable and running smoothly");
  }

  // Money status
  let moneyStatus;
  if (pendingPayments.length === 0 && collectedThisWeek > 0) {
    moneyStatus = "Great!";
  } else if (pendingPayments.length <= 2) {
    moneyStatus = "Good";
  } else {
    moneyStatus = "Needs Follow-up";
  }

  // Bottom line
  let bottomLine;
  if (overall === "Excellent") {
    bottomLine = `All ${totalUnits} properties running smoothly. ${occupancyRate}% occupancy. Keep it up!`;
  } else if (overall === "Good") {
    bottomLine = `Most things are in order. Focus on ${priorities[0]?.toLowerCase() || "staying on track"}.`;
  } else {
    bottomLine = `Several items need attention this week. Prioritize the ${problems.length} issue(s) flagged above.`;
  }

  return {
    greeting: `Good morning! Here's your ${dayNames[now.getDay()]} briefing.`,
    date: now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
    status: {
      overall,
      occupancy: `${occupancyRate}%`,
      properties: totalUnits,
    },
    money: {
      collected_this_week: collectedThisWeek,
      message: totalPending > 0
        ? `$${totalPending.toLocaleString()} still pending`
        : "All payments collected!",
      status: moneyStatus,
    },
    priorities,
    problems,
    wins,
    bottom_line: bottomLine,
  };
}

function MondayReport() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [user]);

  const fetchReport = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [properties, payments, maintenance, leases, tenants] = await Promise.all([
        propertyService.getAll(user.uid),
        paymentService.getAll(user.uid),
        maintenanceService.getAll(user.uid),
        leaseService.getAll(user.uid),
        tenantService.getAll(user.uid),
      ]);

      const reportData = generateReport(properties, payments, maintenance, leases, tenants);
      setReport(reportData);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error("Error fetching Monday report:", error);
    } finally {
      setLoading(false);
    }
  };

  const emailReport = async () => {
    // In real app, would send email via a cloud function
    alert("Report sent to your email!");
  };

  const getStatusColor = (status) => {
    if (status.includes("Excellent")) return "success";
    if (status.includes("Good")) return "warning";
    if (status.includes("attention")) return "error";
    return "default";
  };

  if (loading) {
    return (
      <Card sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading your Monday report...</Typography>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card sx={{ p: 3, textAlign: "center" }}>
        <Typography>Unable to load report</Typography>
        <Button onClick={fetchReport} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Monday Morning Report
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {report.greeting}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {report.date} • Last updated: {lastUpdated}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton onClick={fetchReport} size="large">
              <Refresh />
            </IconButton>
            <IconButton onClick={emailReport} size="large">
              <Email />
            </IconButton>
            <IconButton onClick={() => window.print()} size="large">
              <Print />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Overall Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: "fit-content" }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <TrendingUp sx={{ mr: 1 }} />
              Overall Status
            </Typography>

            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h3" fontWeight="bold">
                {report.status.overall}
              </Typography>
              <Chip
                label={`${report.status.occupancy} occupancy`}
                color={getStatusColor(report.status.overall)}
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {report.status.properties} properties
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Money Summary */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: "fit-content" }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <AttachMoney sx={{ mr: 1 }} />
              Money This Week
            </Typography>

            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                ${report.money.collected_this_week.toLocaleString()}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {report.money.message}
              </Typography>
              <Chip
                label={report.money.status}
                color={report.money.status === "Great!" ? "success" : "warning"}
                sx={{ mt: 1 }}
              />
            </Box>
          </Card>
        </Grid>

        {/* Priorities */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <PriorityHigh sx={{ mr: 1, color: "warning.main" }} />
              This Week&apos;s Priorities
            </Typography>

            <List>
              {report.priorities.map((priority, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Typography variant="h6" color="warning.main">
                      {index + 1}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={priority} />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Problems */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <Warning sx={{ mr: 1, color: "error.main" }} />
              Needs Attention
            </Typography>

            {report.problems.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CheckCircle sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
                <Typography variant="body1" color="success.main">
                  Nothing urgent!
                </Typography>
              </Box>
            ) : (
              <List>
                {report.problems.map((problem, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Warning
                        sx={{
                          color: problem.urgency === "high" ? "error.main" : "warning.main"
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={problem.property}
                      secondary={problem.issue}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Card>
        </Grid>

        {/* Quick Wins */}
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <Celebration sx={{ mr: 1, color: "success.main" }} />
              Wins This Week
            </Typography>

            <Grid container spacing={2}>
              {report.wins.map((win, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: "center",
                      bgcolor: "success.light",
                      color: "success.contrastText"
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {win}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>

        {/* Bottom Line */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              textAlign: "center",
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              color: "white"
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Bottom Line
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              {report.bottom_line}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MondayReport;
